import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Eye, EyeOff, Send } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

interface PreviewResp {
  valid: boolean;
  reason?: string;
  email?: string;
  role?: string;
  accountName?: string;
  expiresAt?: string;
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token") ?? "";
  const [preview, setPreview] = useState<PreviewResp | null>(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setPreview({ valid: false, reason: "missing_token" });
      return;
    }
    api
      .get<PreviewResp>(`/v1/team/invitations/preview?token=${encodeURIComponent(token)}`)
      .then(setPreview)
      .catch(() => setPreview({ valid: false, reason: "network_error" }));
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/v1/team/invitations/accept", { token, password });
      await refresh();
      setDone(true);
      // Redirect ke dashboard setelah 1 detik
      setTimeout(() => navigate("/"), 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "unknown_error");
    } finally {
      setBusy(false);
    }
  }

  if (!preview) {
    return (
      <div className="min-h-full flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }

  if (!preview.valid) {
    return (
      <main className="min-h-full flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-brand-50">
        <div className="max-w-md w-full bg-white rounded-2xl border border-mailtera-border shadow-sm p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-50 flex items-center justify-center mb-4">
            <AlertCircle size={26} className="text-rose-600" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-1">
            Invitation tidak valid
          </h1>
          <p className="text-sm text-slate-600">
            {preview.reason === "expired"
              ? "Link ini sudah kadaluarsa. Minta admin resend."
              : preview.reason === "already_accepted"
                ? "Undangan ini sudah pernah dipakai."
                : "Link tidak ditemukan. Kemungkinan salah URL atau invitation sudah dihapus."}
          </p>
          <a
            href="/login"
            className="mt-6 inline-block text-sm text-brand-700 hover:underline"
          >
            → Login
          </a>
        </div>
      </main>
    );
  }

  if (done) {
    return (
      <main className="min-h-full flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-brand-50">
        <div className="max-w-md w-full bg-white rounded-2xl border border-mailtera-border shadow-sm p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
            <CheckCircle2 size={26} className="text-emerald-600" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-1">
            Welcome!
          </h1>
          <p className="text-sm text-slate-600">
            Redirecting ke dashboard…
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-full flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-brand-50">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-1">
            <div className="w-10 h-10 rounded-xl bg-brand-600 flex items-center justify-center">
              <Send size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <div className="text-2xl font-semibold text-brand-600 tracking-tight">
              Mailtera Send
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-mailtera-border shadow-sm p-8">
          <h1 className="text-xl font-semibold text-brand-600 mb-1">
            Terima undangan
          </h1>
          <p className="text-sm text-mailtera-text-muted mb-5">
            Anda diundang ke tim{" "}
            <strong className="text-slate-900">{preview.accountName}</strong>{" "}
            sebagai <code className="font-mono text-brand-700 bg-brand-50 border border-brand-100 px-1.5 py-0.5 rounded text-xs">{preview.role}</code>.
          </p>

          <div className="text-xs text-slate-500 bg-slate-50 border border-mailtera-border rounded-lg px-3 py-2 mb-5">
            Email login: <span className="font-mono text-slate-900">{preview.email}</span>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-mailtera-text">
                Set password Anda
              </label>
              <div className="relative mt-1">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={12}
                  autoComplete="new-password"
                  autoFocus
                  className="w-full h-11 pr-10 pl-3 border border-mailtera-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              <div className="text-[11px] text-mailtera-text-muted mt-1">
                Minimal 12 karakter. Password ini yang Anda pakai login.
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || password.length < 12}
              className="w-full h-11 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg transition disabled:opacity-60"
            >
              {busy ? "Memproses…" : "Terima & masuk"}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-mailtera-text-muted">
          Kalau Anda tidak kenal undangan ini, abaikan tab ini — tidak ada yang berubah.
        </p>
      </div>
    </main>
  );
}
